import chroma from 'chroma-js';
import React, { FC, HTMLAttributes, useCallback, useMemo, useState } from 'react';
import { createSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Button, Chip, Icon, Tooltip, formatNumber } from '@column/column-ui-kit';
import { DataTable, type DataTableColumnDef } from '@column/ui';

import {
  TransferSnapshotFilters,
  useTransferSnapshot,
  useTransferSnapshots,
} from '../../Transfers/_hooks/useTransferSnapshots';
import { ROUTE } from '~/app/routes';
import { RelativeTime } from '~/elements';
import { useViewTransition } from '~/hooks/useViewTransition';
import { useNavigate } from '~/lib/navigation';
import { TransferStatusChip } from '~/lib/transfers/components/TransferStatusChip';
import { formatTransferType, getTransferIcon, getTransferURL } from '~/lib/transfers/utils';
import { convertTransferAmount } from '~/repositories/Transfer';
import { UserTransferFilterSnapshot } from '~/repositories/UserRepository';
import { Paragraph, Text } from '~/styles';

import { TransferSnapshotModal } from './TransferSnapshotModal';

const Wrapper = styled.div`
  margin: 24px 0;
  position: relative;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--page-margin) 16px;
  height: 36px;
`;

const Grid = styled.div`
  display: grid;
  gap: 16px;
  padding: 0 var(--page-margin);
  grid-template-columns: auto;

  @media (min-width: 1080px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1480px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const CardWrapper = styled.div`
  position: relative;
  border-radius: 8px;
  box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(150)};
  background-color: ${({ theme }) => theme.secondary.blendToBackground(25)};
`;

const ButtonWrapper = styled.div`
  transition:
    opacity 0.2s,
    transform 0.2s;
  transform-origin: 0 50%;
  opacity: 0;
  transform: translateX(-4px) scale(0.95);
`;

const ButtonEditWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const CardTitle = styled.div`
  padding: 8px 16px;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 8px;
  box-sizing: border-box;
  align-items: center;
  width: 100%;
  line-height: 32px;
  border-radius: 8px 8px 0 0;
  background-color: ${({ theme }) => theme.background};
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

  &:hover:not(:has(${ButtonEditWrapper}:hover)) {
    ${ButtonWrapper} {
      opacity: 1;
      transform: translateZ(0);
    }
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const Title = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  min-width: 0;
  flex: 1;
`;

const CardListItemIcon = styled.div`
  svg {
    --icon-size: 16px;
  }

  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  display: flex;
  align-items: center;
`;

const CardListEmpty = styled.div`
  padding: 16px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

export const TransferAmount = styled.div<{ $isAdding?: boolean }>`
  font-size: 14px;
  line-height: 14px;
  letter-spacing: -0.02em;
  font-weight: 500;
  display: flex;
  gap: 4px;
  align-items: baseline;
  color: ${({ theme, $isAdding }) =>
    $isAdding
      ? chroma(theme.success.background)
          .darken(theme.id !== 'dark' ? 0.75 : 0)
          .hex()
      : theme.foreground};

  ${({ $isAdding }) =>
    $isAdding === false &&
    css`
      position: relative;

      &:before {
        content: '-';
        position: absolute;
        right: 100%;
        display: block;
        top: -1px;
        font: inherit;
        line-height: inherit;
        z-index: 10;
      }
    `}
`;

const Placeholder = styled.div`
  position: absolute;
  inset: 20% 0 0 0;
  background-image: linear-gradient(to bottom, transparent, ${({ theme }) => theme.background} 80%);
  z-index: 1;
  display: grid;
  align-items: end;
  grid-template-columns: 1fr auto;
  grid-gap: 8px;
  padding: 16px;
`;

const PlaceholderContent = styled.div`
  font-size: 10px;
  font-weight: 400;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};

  strong {
    color: ${({ theme }) => theme.foreground};
    font-weight: 500;
  }
`;

const Empty = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto;
  gap: 16px;
  padding: 0 var(--page-margin);

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 280px auto;
    gap: 24px;
  }
`;

const EmptyList = styled.div`
  position: relative;
  width: 100%;
  height: 164px;
  flex-shrink: 0;
  padding: 20px 8px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.secondary.blendToBackground(25)};
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(125)};
  border-radius: 13px;
  display: grid;
  align-content: center;
  gap: 8px;
`;

const EmptyEntry = styled.div`
  display: grid;
  grid-template-columns: 12px 32px 44px 42px 20px;
  align-items: center;
  gap: 8px;
  width: 180px;
  margin: 0 auto;
  background-color: ${({ theme }) => theme.secondary.blendToBackground(0, 300)};
  border-radius: 7px;
  padding: 10px 12px;
  border: 1px solid hsla(230, 30%, 5%, 0.05);

  &:nth-child(2) {
    transform-origin: 50% 50%;
    z-index: 1;
    width: 200px;
    background-color: ${({ theme }) => theme.background};
    backdrop-filter: blur(4px);
    border: 1px solid hsla(230, 30%, 5%, 0.1);

    span {
      opacity: 1;
    }
  }

  span {
    background-color: ${({ theme }) => theme.secondary.blendToBackground(200)};
    border-radius: 2.5px;
    height: 6px;
    opacity: 0.5;

    &:nth-child(1) {
      height: 12px;
    }

    &:nth-child(2) {
      background-color: ${({ theme }) => theme.secondary.blendToBackground(400)};
    }

    &:nth-child(4) {
      background-color: ${({ theme }) => theme.success.blendToBackground(500)};
    }
  }
`;

const EmptyContent = styled.div`
  display: grid;
  gap: 8px;
  max-width: 400px;
`;

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  name: string;
  filters?: TransferSnapshotFilters;
  index: number;
  isPlaceholder?: boolean;
  onRefresh?: () => void;
  onDismiss?: () => void;
}

const Card: FC<CardProps> = ({ name, filters, index, onRefresh, isPlaceholder, onDismiss, ...props }) => {
  const { response, createRequest, isLoading } = useTransferSnapshot({
    initialParams: { limit: 5, ...filters } as any,
  });

  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleSave = useCallback(
    (data: Partial<UserTransferFilterSnapshot>) => {
      createRequest({
        ...data.filters,
        limit: 5,
      } as TransferSnapshotFilters & { limit: number }).then(() => {
        onRefresh?.();
      });
    },
    [createRequest, onRefresh]
  );

  const handleCardSpecificRefresh = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  const viewAllTransfers = useCallback(() => {
    if (!filters) {
      navigate(ROUTE.TRANSFERS);

      return;
    }

    const cleanedFilters: Record<string, string> = Object.entries(filters)
      .filter(([, value]) => value !== undefined && value !== null)
      .reduce(
        (acc, [key, value]) => {
          acc[key] = Array.isArray(value) ? value.join(',') : String(value);
          return acc;
        },
        {} as Record<string, string>
      );

    const searchParams = createSearchParams(cleanedFilters);

    navigate(`${ROUTE.TRANSFERS}?${searchParams.toString()}`);
  }, [navigate, filters]);

  const handleClickTransfer = useCallback(
    (transferId: string, transferType?: string) => {
      navigate(getTransferURL(transferId, (transferType || 'ach') as any));
    },
    [navigate]
  );

  type TableRowData = {
    id: string;
    icon: React.ReactNode;
    amount: number;
    type: string;
    status: string;
    createdAt: string;
    isAdding?: boolean;
    transferId: string;
    transferType: string;
  };

  const tableData = useMemo<TableRowData[]>(() => {
    if (!response?.transfers) {
      return [];
    }

    return response.transfers.map((transfer) => {
      const convertAmount = convertTransferAmount(transfer.type, transfer.isIncoming);
      const isAdding =
        transfer.type === 'book'
          ? filters?.bankAccountId
            ? transfer.senderInternalAccount.bankAccountId === filters?.bankAccountId
              ? false
              : true
            : undefined
          : convertAmount !== -1;

      let icon;
      if (transfer.type === 'check_credit') {
        icon = <Icon.CircleArrowDown />;
      } else if (transfer.type === 'check_debit') {
        icon = <Icon.CircleArrowUp />;
      } else if (transfer.type === 'book') {
        icon = <Icon.Swap />;
      } else if (transfer.isIncoming) {
        icon = <Icon.CircleArrowDown />;
      } else {
        icon = <Icon.CircleArrowUp />;
      }

      return {
        id: transfer.id,
        icon,
        amount: transfer.amount,
        type: transfer.type,
        status: transfer.status,
        createdAt: transfer.createdAt,
        isAdding,
        transferId: transfer.id,
        transferType: transfer.type,
      };
    });
  }, [response?.transfers, filters?.bankAccountId]);

  const columns = useMemo<DataTableColumnDef<TableRowData>[]>(
    () => [
      {
        accessor: 'icon',
        header: '',
        width: 'min-content',
        cell: ({ value }) => <CardListItemIcon>{value}</CardListItemIcon>,
      },
      {
        accessor: 'amount',
        header: '',
        width: 'minmax(100px, 1fr)',
        cell: ({ value, row }) => <TransferAmount $isAdding={row.isAdding}>{formatNumber(value ?? 0)}</TransferAmount>,
      },
      {
        accessor: 'type',
        header: '',
        width: 'minmax(min-content, max-content)',
        cell: ({ value }) => (
          <Chip variant="subtle" size="small" icon={getTransferIcon(value)}>
            {formatTransferType(value as any)}
          </Chip>
        ),
      },
      {
        accessor: 'status',
        header: '',
        width: 'minmax(min-content, max-content)',
        cell: ({ value }) => <TransferStatusChip status={value} size="small" />,
      },
      {
        accessor: 'createdAt',
        header: '',
        width: 'minmax(min-content, max-content)',
        cell: ({ value }) => <RelativeTime timestamp={value} preventHover />,
      },
    ],
    []
  );

  const handleRowClick = useCallback(
    (row: TableRowData) => {
      handleClickTransfer(row.transferId, row.transferType);
    },
    [handleClickTransfer]
  );

  return (
    <>
      <TransferSnapshotModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        id={isPlaceholder ? undefined : index}
        name={name}
        filters={filters}
        onSave={handleSave}
        onRefresh={handleCardSpecificRefresh}
      />

      <CardWrapper {...props}>
        <CardTitle>
          <TitleWrapper>
            <Title>{name}</Title>

            {response?.transfers && response?.transfers?.length > 0 && !isPlaceholder && (
              <ButtonWrapper>
                <Button
                  variant="subtle-primary"
                  size="small"
                  onClick={viewAllTransfers}
                  icon={<Icon.ChevronRight />}
                  iconRight
                >
                  All Transfers
                </Button>
              </ButtonWrapper>
            )}
          </TitleWrapper>
          <ButtonEditWrapper>
            {isPlaceholder ? (
              <>
                <Button variant="secondary" size="tiny" onClick={onDismiss}>
                  Dismiss
                </Button>
              </>
            ) : (
              <Tooltip content="Edit Snapshot">
                <Button variant="subtle" size="small" onClick={() => setIsOpen(true)} icon={<Icon.Edit />} />
              </Tooltip>
            )}
          </ButtonEditWrapper>
        </CardTitle>

        {!response || isLoading || (response?.transfers && response?.transfers?.length > 0) ? (
          <>
            <DataTable<TableRowData>
              columns={columns}
              data={tableData}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              rowKey={(row) => row.id}
              className="rounded-none border-0"
              headerClassName="hidden!"
              rowClassName="min-h-12"
            />
            {response?.transfers?.length === 0 && !isLoading && <CardListEmpty>No transfers found</CardListEmpty>}
          </>
        ) : (
          <CardListEmpty>No transfers found</CardListEmpty>
        )}

        {isPlaceholder && (
          <Placeholder>
            <PlaceholderContent>
              <strong>Snapshots provide a quick look into transfer activity.</strong> Save your preferred filters.
            </PlaceholderContent>

            <Button variant="primary" size="small" onClick={() => setIsOpen(true)} icon={<Icon.Plus />}>
              Create
            </Button>
          </Placeholder>
        )}
      </CardWrapper>
    </>
  );
};

export const TransferSnapshots: FC = () => {
  const { transferSnapshots, fetchTransferSnapshots } = useTransferSnapshots();
  const [isOpen, setIsOpen] = useState(false);

  const isEmpty = useViewTransition(!transferSnapshots || transferSnapshots.length === 0);

  const handleRefresh = useCallback(() => {
    fetchTransferSnapshots();
  }, [fetchTransferSnapshots]);

  const handleCreateSnapshot = useCallback(() => {
    setIsOpen(true);
  }, []);

  return (
    <Wrapper>
      <TransferSnapshotModal isOpen={isOpen} onClose={() => setIsOpen(false)} />

      <Toolbar>
        <Text size={16} weight="medium" color="foreground" whiteSpace="nowrap">
          Transfer Snapshots
        </Text>

        {transferSnapshots && transferSnapshots.length > 0 && (
          <Button variant="primary" size="small" onClick={handleCreateSnapshot} icon={<Icon.Plus />}>
            Create Snapshot
          </Button>
        )}
      </Toolbar>

      {isEmpty ? (
        <Empty>
          <EmptyList>
            {Array.from({ length: 3 }).map((_, i) => (
              <EmptyEntry key={i}>
                <span />
                <span />
                <span />
                <span />
                <span />
              </EmptyEntry>
            ))}
          </EmptyList>

          <EmptyContent>
            <Text size={16} weight="medium" color="foreground" whiteSpace="nowrap">
              No snapshots found
            </Text>
            <Paragraph>Snapshots provide a quick look into transfer activity. Save your preferred filters.</Paragraph>
            <div>
              <Button variant="secondary" size="small" onClick={() => setIsOpen(true)} icon={<Icon.Plus />}>
                Snapshot
              </Button>
            </div>
          </EmptyContent>
        </Empty>
      ) : (
        <Grid>
          {transferSnapshots.map((snapshot, index: number) => (
            <Card
              key={`snapshot-${index}-${snapshot.name}-${snapshot.order}`}
              name={snapshot.name}
              filters={snapshot.filters}
              index={index}
              onRefresh={handleRefresh}
            />
          ))}
        </Grid>
      )}
    </Wrapper>
  );
};
